import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AlbumGrid from "@/components/AlbumGrid";
import { ALBUMS } from "@/data/photos";
import { motion } from "framer-motion";
import { Images, ChevronDown, ChevronUp } from "lucide-react";

export default function Galerie() {
  const [openAlbum, setOpenAlbum] = useState<string | null>(ALBUMS[0].id);
  const navigate = useNavigate();

  const albumRoutes: Record<string, string> = {
    portraits: "/fondatrice",
    ceremonies: "/ceremonies",
    actions: "/actions",
    events: "/actions",
    equipe: "/equipe",
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      {/* @section: galerie-hero */}
      <section
        className="relative pt-36 pb-20 overflow-hidden"
        style={{ background: "linear-gradient(135deg, oklch(0.28 0.09 240) 0%, oklch(0.2 0.07 240) 100%)" }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-accent/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white/3 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/40 bg-accent/10 text-accent text-sm font-medium mb-6">
              <Images className="w-4 h-4" />
              {ALBUMS.reduce((s, a) => s + a.photos.length, 0)} photos · {ALBUMS.length} albums
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Galerie <span className="text-accent">FGNB</span>
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed">
              Explorez l'ensemble des albums photos de la Fondation Gath Naël Beko — portraits, cérémonies, actions humanitaires, événements et équipe.
            </p>
          </motion.div>

          {/* Album thumbnails row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-12"
          >
            {ALBUMS.map((album) => (
              <button
                key={album.id}
                onClick={() => setOpenAlbum(album.id === openAlbum ? null : album.id)}
                className={`relative rounded-2xl overflow-hidden aspect-square group transition-all duration-300 ${openAlbum === album.id ? "ring-2 ring-accent scale-[1.02]" : "opacity-80 hover:opacity-100 hover:scale-[1.01]"}`}
              >
                <img src={album.cover} alt={album.title} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 bg-gradient-to-t ${album.color} transition-opacity duration-300`} />
                <div className="absolute inset-0 flex flex-col items-center justify-end p-3">
                  <p className="text-white text-xs font-bold text-center leading-tight">{album.title}</p>
                  <p className="text-white/60 text-[10px]">{album.photos.length} photos</p>
                </div>
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Albums content */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-12">
          {ALBUMS.map((album, i) => (
            <motion.div
              key={album.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="border border-border rounded-3xl overflow-hidden"
            >
              {/* Album header */}
              <button
                className="w-full flex items-center justify-between p-6 bg-card hover:bg-muted/30 transition-colors"
                onClick={() => setOpenAlbum(openAlbum === album.id ? null : album.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={album.cover} alt={album.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-foreground" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{album.title}</h3>
                    <p className="text-sm text-muted-foreground">{album.subtitle} · <span className="text-accent font-medium">{album.photos.length} photos</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(albumRoutes[album.id]); }}
                    className="hidden sm:inline-flex px-4 py-2 rounded-full text-xs font-semibold border border-accent/30 text-accent hover:bg-accent/10 transition-colors"
                  >
                    Voir la page
                  </button>
                  {openAlbum === album.id
                    ? <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  }
                </div>
              </button>

              {/* Album grid */}
              {openAlbum === album.id && (
                <div className="p-6 border-t border-border bg-background">
                  <AlbumGrid photos={album.photos} columns={3} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
